import { observer } from 'mobx-react';
import * as React from 'react';
import { ProviderWorkerReportMetric } from '../../api/server/models/ProviderWorkerReportMetric';
import { ProviderWorkerReportWorkflow } from '../../api/server/models/ProviderWorkerReportWorkflow';
import { formatDate } from '../../services/primitives/date-utils';
import { WEEK_COLUMNS, WORKFLOW_LABELS, WORKFLOW_ORDER } from '../../services/worker-report/worker-report.model';
import { WorkerReportStore } from '../../stores/worker-report/worker-report.store';
import { BaseSelect, ISelectOption } from '../base/select/select';
import { TypeFilterBar, TypeFilterChip } from '../base/type-filter-bar/type-filter-bar';
import { StatCard } from '../stat-card/stat-card';
import { METRIC_DOTS, WORKFLOW_HUES } from './workflow-colors';
import { WorkflowDrawer } from './workflow-drawer';
import { WorkflowHeatmap } from './workflow-heatmap';
import * as S from './workflows.styles';

export interface IWorkflowsPageProps {
    store: WorkerReportStore;
    /** The page frame, row and card each console draws its pages with. */
    layout: {
        Page: React.ComponentType<{ children?: React.ReactNode }>;
        Row: React.ComponentType<{ children?: React.ReactNode }>;
        Card: React.ComponentType<{ size: 'large'; children?: React.ReactNode }>;
    };
    /** The claim's page on this console. */
    claimHref: (claimId: string) => string;
}

const UNKNOWN_COUNT = 'Unknown: the worker report did not load.';

interface ICardSpec {
    metric: ProviderWorkerReportMetric | undefined;
    label: string;
    sub: string;
    value: (summary: NonNullable<WorkerReportStore['summary']>) => number;
}

const CARDS: ICardSpec[] = [
    { metric: undefined, label: 'Total claims', sub: 'Claims in the current snapshot', value: summary => summary.total },
    {
        metric: ProviderWorkerReportMetric.INPROGRESS,
        label: 'In progress',
        sub: 'Moving through workflow (Paused excluded)',
        value: summary => summary.inProgress,
    },
    { metric: ProviderWorkerReportMetric.WAITING, label: 'Waiting', sub: 'Paused on a waiting action', value: summary => summary.waiting },
    { metric: ProviderWorkerReportMetric.ERROR, label: 'Errors', sub: 'In an error action', value: summary => summary.errors },
];

const ALL_STEPS: ISelectOption<string> = { label: 'All steps', value: undefined };

/**
 * Workflows (FE-167): where every active claim sits in the worker, and for how long. Built once in core
 * and mounted by both consoles, each with its own claim endpoint (`IWorkerReportApi`) and routes.
 *
 * The report is read once per visit. Filters, card selection and expanding rows only re-cut what is
 * loaded; clicking a box asks for that box's claims and opens them in the drawer.
 */
export const WorkflowsPage: React.FC<IWorkflowsPageProps> = observer(({ store, layout, claimHref }) => {
    const { Page, Row, Card } = layout;

    React.useEffect(() => {
        void store.load();
        return (): void => store.closeDrawer();
    }, [store]);

    const summary = store.summary;
    const report = store.report;
    const hiddenOlder = summary ? summary.total - store.heatmap.rows.reduce((sum, row) => sum + row.total, 0) : 0;
    const stepValue = store.step
        ? { value: store.step, label: store.steps.find(option => option.value === store.step)?.label || store.step }
        : ALL_STEPS;

    return (
        <Page>
            <Row>
                <S.Header>
                    <S.Title>Workflows</S.Title>
                    <S.Snapshot>
                        {report ? `Snapshot ${formatDate(report.generatedAt, 'h:mm a, MMM d yyyy')} · ` : ''}
                        Active claims, worker state changed in last 365 days
                    </S.Snapshot>
                </S.Header>
            </Row>
            <Row>
                <S.Cards>
                    {CARDS.map(card => (
                        <S.CardButton
                            key={card.label}
                            variant="unstyled"
                            selected={store.metric === card.metric}
                            disabled={!summary}
                            aria-label={`Show ${card.label.toLowerCase()}`}
                            aria-pressed={store.metric === card.metric}
                            onClick={(): void => store.setMetric(card.metric)}>
                            <StatCard
                                label={card.label.toUpperCase()}
                                value={summary ? card.value(summary).toLocaleString() : undefined}
                                unavailableReason={UNKNOWN_COUNT}
                                sub={
                                    <S.CardLabel>
                                        <S.Dot color={METRIC_DOTS[card.metric || 'TOTAL']} />
                                        {card.sub}
                                    </S.CardLabel>
                                }
                            />
                        </S.CardButton>
                    ))}
                </S.Cards>
            </Row>
            <Row>
                <Card size="large">
                    <S.ActivityHead>
                        <div>
                            <S.ActivityTitle>Worker Activity</S.ActivityTitle>
                            <S.ActivitySub>
                                Claim age by workflow status. Click a status to expand actions; click a box to load its claims.
                            </S.ActivitySub>
                        </div>
                        <S.Filters>
                            <S.FilterLabel>Workflow</S.FilterLabel>
                            <TypeFilterBar>
                                <TypeFilterChip active={!store.workflow} onClick={(): void => store.setWorkflow(undefined)}>
                                    All
                                </TypeFilterChip>
                                {WORKFLOW_ORDER.map((workflow: ProviderWorkerReportWorkflow) => (
                                    <TypeFilterChip
                                        key={workflow}
                                        active={store.workflow === workflow}
                                        onClick={(): void => store.setWorkflow(store.workflow === workflow ? undefined : workflow)}>
                                        <S.Swatch color={WORKFLOW_HUES[workflow]} />
                                        {WORKFLOW_LABELS[workflow]}
                                    </TypeFilterChip>
                                ))}
                            </TypeFilterBar>
                            <S.FilterLabel>Step</S.FilterLabel>
                            <S.StepSelect>
                                <BaseSelect
                                    aria-label="Step"
                                    required
                                    isSearchable
                                    options={[ALL_STEPS, ...store.steps]}
                                    value={stepValue}
                                    disabled={!report}
                                    onChange={(option): void => store.setStep((option as ISelectOption<string> | null)?.value || undefined)}
                                />
                            </S.StepSelect>
                        </S.Filters>
                    </S.ActivityHead>
                    <WorkflowHeatmap store={store} />
                    <S.Legend>
                        <S.LegendItems>
                            Workflow
                            {WORKFLOW_ORDER.map(workflow => (
                                <S.LegendItem key={workflow}>
                                    <S.Swatch color={WORKFLOW_HUES[workflow]} />
                                    {WORKFLOW_LABELS[workflow]}
                                </S.LegendItem>
                            ))}
                        </S.LegendItems>
                        <span>
                            {hiddenOlder > 0 && !store.metric
                                ? `${hiddenOlder.toLocaleString()} claims older than ${WEEK_COLUMNS} weeks are counted above but not drawn · `
                                : ''}
                            Shade = volume · corner = secondary workflow
                        </span>
                    </S.Legend>
                </Card>
            </Row>
            <WorkflowDrawer store={store} claimHref={claimHref} />
        </Page>
    );
});
